import {type FC, type ReactNode, useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {FocusContext, init, setKeyMap, useFocusable} from '@noriginmedia/norigin-spatial-navigation';
import useEscape from '../hooks/useEscape.tsx';
import useKeyboards from '../hooks/useKeyboards.tsx';

const MainLayoutStyleWrapper = styled.div<{
    $isCursorHidden: boolean;
}>`
    display: flex;
    padding: 3vh;
    gap: 2vh;
    height: 100vh;
    flex-direction: row;
    background-color: #171717;
    cursor: ${({$isCursorHidden}) => ($isCursorHidden ? 'none !important' : 'inherit')};
`;

interface MainLayoutProps {
    children: ReactNode;
}

const MainLayout: FC<MainLayoutProps> = ({children}) => {
    // 마우스 hide
    const [isCursorHidden, setIsCursorHidden] = useState<boolean>(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // 커서 숨기기 함수
    const hideCursor = useCallback(() => {
        setIsCursorHidden(true);
    }, []);

    // 커서 보이기 및 타이머 재설정 함수
    const showCursorAndResetTimer = useCallback(() => {
        setIsCursorHidden(false);

        // 기존 타이머 클리어
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // 새로운 타이머 설정 (3초 후 숨기기)
        timeoutRef.current = setTimeout(hideCursor, 3000);
    }, [hideCursor]);

    useEffect(() => {
        // 초기 타이머 설정
        timeoutRef.current = setTimeout(hideCursor, 3000);

        // 마우스 움직임 이벤트 리스너 등록
        const handleMouseMove = () => {
            showCursorAndResetTimer();
        };

        document.addEventListener('mousemove', handleMouseMove);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            document.removeEventListener('mousemove', handleMouseMove);
        };
    }, [hideCursor, showCursorAndResetTimer]);

    const {
        ref,
        focusKey: menuFocusKey,
        focusSelf: focusMenu,
    } = useFocusable({
        focusKey: 'MENU_CONTAINER',
        trackChildren: true,
    });

    useEffect(() => {
        init({
            // debug: true,
            // visualDebug: true,
            distanceCalculationMethod: 'center',
        });

        setKeyMap({
            left: ['ArrowLeft', 37],
            up: ['ArrowUp', 38],
            right: ['ArrowRight', 39],
            down: ['ArrowDown', 40],
            enter: ['Enter', 13],
        });
    }, []);

    const [msg, setMsg] = useState('아직 누르지 않음');
    useKeyboards(
        (key, e) => {
            if (key === 'j') {
                setMsg('J 눌림 — 이전 항목으로 이동');
            } else if (key === 'k') {
                setMsg('K 눌림 — 다음 항목으로 이동');
            } else if (key === 'l') {
                setMsg('L 눌림 — 좋아요 토글');
            }
        },
        {keys: ['j', 'k', 'l'], enableInInputs: false}
    );

    useEffect(() => {
        focusMenu();
    }, [focusMenu]);

    useEscape();

    return (
        <FocusContext.Provider value={menuFocusKey}>
            <MainLayoutStyleWrapper ref={ref} $isCursorHidden={isCursorHidden}>
                {children}
            </MainLayoutStyleWrapper>
        </FocusContext.Provider>
    );
};

export default MainLayout;
